import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'core/routing';
import { clientStore } from 'apps/client/app';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import {
  Button,
  ConfirmModal,
  Heading,
  Icon,
  IconButton,
  OverflowTooltip,
} from 'components';
import Tooltip from 'components/Tooltip';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import { themes } from 'styles/themes/wikifactoryNew';
import { imageSrc } from 'apps/file/utils';
import { conf as mainConf } from 'apps/config.yaml';
import { ThinCardTag } from 'legacyComponents/cards/styles';
import { WorkspaceSettings } from 'icons';
import { SpaceLink } from 'apps/shared';
import i18next from 'i18next';
import {
  ColumnBlock,
  ActionsWrapper,
  ImageWrapper,
  MainShareCardWrapper,
} from 'apps/share/blocks/shareCard.styles';
import { ShareItemProps } from 'apps/share/blocks/common';
import { useGetSystemCondition } from 'hooks';
import { getSystemCondition } from 'apps/routes/utils';

type ProjectItemProps = {
  cardObject: Record<string, any>;
  cardApp: Record<string, any>;
  cardColumns: number;
  cardNo: number;
  projectManager?: any;
};

export const ProjectListItem = observer(
  ({ cardObject, cardApp, cardNo }: ProjectItemProps) => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [hoverId, setHoverId] = useState('');
    const { shouldShowFrameSideBar } = useGetSystemCondition();

    const handleClickDeleteIcon = () => {
      setOpenDeleteModal(true);
    };

    const closeDeleteModal = () => {
      setOpenDeleteModal(false);
    };

    if (cardObject === null) {
      return <CardIndicator type='list' />;
    }

    const {
      image,
      contributionCount,
      slug,
      spacePath,
      tags,
      space,
      content,
      name,
      id,
      canUpdate,
      canDelete,
      fileSyncConnected,
      private: isPrivate,
      contributors,
    } = cardObject;

    const { totalCount: contributorTotal = 0 } = contributors || {};
    const { followersCount } = content;
    const spaceType = spacePath[0] === '+' ? 'initiative' : 'profile';
    const route = isInDesktopMode()
      ? `${spaceType}.project.files`
      : `${spaceType}.project`;

    const hasPermission = canUpdate || canDelete;
    const showHoverState = hoverId === id && hasPermission;

    const projectSettingRouteName = `${spaceType}.project.settings.teams`;

    // Only show 3 tags as a workaround before a proper tag overflow system
    const MAX_NUM_TAGS = 3;
    const tagsList = (tags || [])
      .slice(0, MAX_NUM_TAGS)
      .map((tag: { id: string; name: string }, index: number) => {
        if (index >= MAX_NUM_TAGS - 1) {
          // If we are in the last tag return a "..." tag linking to the project
          return (
            <Link
              name={route}
              params={{ [spaceType]: spacePath, slug }}
              content={content}
              openTab={{
                title: name,
                iconType: 'project',
              }}
            >
              <ThinCardTag>...</ThinCardTag>
            </Link>
          );
        }
        return (
          <Link
            name='channel'
            params={{ channel: tag.name }}
            key={tag?.id ? `${tag.id}_${index}` : index}
            content={{ main: true }}
            className='flex-shrink-0'
          >
            <ThinCardTag>#{tag.name}</ThinCardTag>
          </Link>
        );
      });

    const hideContributionColumn =
      clientStore.windowWidth < 1172 && showHoverState;

    const hideFileSyncTagOrProjectCounter =
      !clientStore.isMobile && clientStore.windowWidth < 880 && showHoverState;

    const fileSyncTag =
      fileSyncConnected &&
      (!hideFileSyncTagOrProjectCounter || clientStore.isMobile) ? (
        <div className='flex  z-50'>
          <div className='flex ml-1 bg-white bg-opacity-90 justify-between items-center border-2 rounded-3xl border-green-300 px-2 py-1 text-black'>
            <div>{i18next.t('Synced')}</div>
            <div className='ml-2 w-3 h-3 rounded-full bg-green-300' />
          </div>
        </div>
      ) : null;

    const hasConnected =
      fileSyncTag ||
      cardApp?.apps?.some((cardItem: ShareItemProps['cardObject']) => {
        return cardItem?.fileSyncConnected;
      });

    const shouldHideProjectCounter =
      (((hasConnected &&
        shouldShowFrameSideBar &&
        clientStore.windowWidth < 1070) ||
        clientStore.isMobile) &&
        showHoverState) ||
      (shouldShowFrameSideBar &&
        clientStore.windowWidth < 920 &&
        showHoverState) ||
      hideFileSyncTagOrProjectCounter ||
      hideContributionColumn;

    const fileSyncMinWidth = 140;
    const columnMinHeight = 40;

    const { showPublicOrPrivateForProjectTag } = getSystemCondition();

    const getNameColumnInfo = () => {
      if (clientStore.isMobile) {
        return {
          maxSize: 14,
          columnWidth: 160,
        };
      }

      if (clientStore.windowWidth > 800 && clientStore.windowWidth < 1070) {
        return {
          maxSize: 30,
          columnWidth: 248,
        };
      }

      return {
        maxSize: 50,
        columnWidth: 384,
      };
    };

    const { columnWidth } = getNameColumnInfo();

    return (
      <MainShareCardWrapper
        style={{
          width: '100%',
          minWidth: 492,
        }}
        data-intercom-target={cardNo === 0 ? 'organisation-projects-list' : ''}
        onMouseEnter={() => {
          setHoverId(cardObject.id);
        }}
        onMouseLeave={() => {
          setHoverId('');
        }}
      >
        <Tooltip id='tooltipBanner' />
        <Link
          name={route}
          params={{ [spaceType]: spacePath, slug }}
          content={content}
          openTab={{
            title: name,
            iconType: 'project',
          }}
          className='flex'
        >
          <div className='relative'>
            <div className='w-full flex shrink-0 absolute items-center flex-col text-center left-0 bottom-0 text-white'>
              {showPublicOrPrivateForProjectTag && (
                <div
                  className='w-full h-4 text-xs select-none'
                  style={{
                    background: isPrivate
                      ? themes.color.wfBlack
                      : themes.color.wfBlue,
                  }}
                >
                  <Icon
                    size='10px'
                    className='mr-[2px] mb-[1.5px]'
                    name={isPrivate ? 'locked' : 'website'}
                  />
                  <span>
                    {isPrivate ? i18next.t('Private') : i18next.t('Public')}
                  </span>
                </div>
              )}
            </div>
            <ImageWrapper>
              <img
                className=' inline-flex  flex-shrink-0'
                src={imageSrc(
                  image,
                  cardObject.getImageFallbackPath(
                    showPublicOrPrivateForProjectTag && isPrivate,
                  ),
                  mainConf.imgSizes.project.card,
                )}
                alt={name}
              />
            </ImageWrapper>
          </div>
        </Link>
        <div className='flex justify-between items-center w-full'>
          <div>
            <div
              className='flex flex-col m-4 h-full'
              style={{ width: columnWidth }}
            >
              <div className='flex items-center'>
                <Link
                  name={route}
                  params={{ [spaceType]: spacePath, slug }}
                  content={content}
                  openTab={{
                    title: name,
                    iconType: 'project',
                  }}
                >
                  <Heading
                    className='hover:text-wfBlue'
                    margin='0 0 small 0'
                    singleLine
                    level='3'
                    data-testid={`project-${name}`}
                  >
                    <OverflowTooltip text={name} tipId={id} />
                  </Heading>
                </Link>
                <div className=' flex mb-2 mx-2 gap-1 overflow-hidden'>
                  {tagsList}
                </div>
              </div>
              <SpaceLink label='Created by' space={space} preferWorkspaceLink />
            </div>
          </div>

          {hasConnected && !hideFileSyncTagOrProjectCounter && (
            <div
              className='sync'
              style={{
                width: fileSyncMinWidth,
                minHeight: columnMinHeight,
              }}
            >
              {fileSyncTag && (
                <Link
                  name={route}
                  params={{ [spaceType]: spacePath, slug }}
                  content={content}
                  openTab={{
                    title: name,
                    iconType: 'project',
                  }}
                >
                  {fileSyncTag}
                </Link>
              )}
            </div>
          )}

          {!shouldHideProjectCounter && (
            <ColumnBlock removePadding className='mx-4'>
              <div
                className='flex items-center justify-start'
                style={{
                  width: 148,
                }}
              >
                <div className='flex items-center cursor-pointer rounded-full py-1 px-2'>
                  <Icon
                    className='mr-1'
                    name='user'
                    data-for='tooltipBanner'
                    data-tip={
                      isPrivate
                        ? i18next.t('Contributors')
                        : i18next.t('Followers')
                    }
                  />
                  {isPrivate ? contributorTotal : followersCount}
                </div>
                <div className='mx-2 flex items-center cursor-pointer rounded-full py-1 px-2'>
                  <Icon
                    className='mr-1'
                    name='contribution'
                    data-for='tooltipBanner'
                    data-tip={i18next.t('Contributions')}
                  />
                  {contributionCount}
                </div>
              </div>
            </ColumnBlock>
          )}

          <ActionsWrapper isVisible={showHoverState}>
            <div
              className='flex justify-end items-center'
              style={{ minWidth: 202 }}
            >
              <Tooltip />
              {canDelete && (
                <Button
                  outline
                  size='small'
                  icon='delete'
                  variant='destructive'
                  onClick={() => {
                    handleClickDeleteIcon();
                  }}
                />
              )}
              {canUpdate && (
                <Link
                  name={projectSettingRouteName}
                  params={{ [spaceType]: spacePath, slug }}
                  content={content}
                  openTab={{
                    title: name,
                    iconType: 'project',
                  }}
                >
                  <IconButton
                    variant='outlined'
                    icon={<WorkspaceSettings viewBox='3 2 18 18' />}
                    style={{
                      marginLeft: 8,
                      color: themes.color.wfBlack,
                      borderColor: themes.color.wfBlack,
                    }}
                  />
                </Link>
              )}
              <Link
                name={route}
                params={{ [spaceType]: spacePath, slug }}
                content={content}
                openTab={{
                  title: name,
                  iconType: 'project',
                }}
                className='inline-flex ml-2'
              >
                <Button
                  outline
                  text='Open'
                  size='small'
                  variant='primary'
                  icon='arrow_right'
                  iconPosition='right'
                />
              </Link>
            </div>
          </ActionsWrapper>
        </div>
        <ConfirmModal
          open={openDeleteModal}
          loading={cardObject.deleting}
          title='Are you sure you want to delete this Project?'
          onConfirm={() => {
            cardObject.ConfirmDeleteByProjectId(id, () => {
              const projectsConn = cardObject.manager;
              projectsConn.Reset();
              projectsConn.loadMore();

              clientStore.UpdateProfileOrTeamWorkspace();
              closeDeleteModal();
            });
          }}
          onClose={() => {
            closeDeleteModal();
          }}
        />
      </MainShareCardWrapper>
    );
  },
);
